import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Col, Card, CardBody, Form, Input, Label, Button, Alert, Badge, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import * as actions from '../../store/actions';
import { toast } from 'react-toastify';

export default function CompanyRegistrationSettings({ data }) {
  const dispatch = useDispatch();
  const { vat_number = '', commercial_register = '', company_name = '', address = '', entity_unified_no = '', id } = data || {};

  const [vatNumber, setVatNumber] = useState(vat_number || '');
  const [commercialRegister, setCommercialRegister] = useState(commercial_register || '');
  const [companyName, setCompanyName] = useState(company_name || '');
  const [companyAddress, setCompanyAddress] = useState(address || '');
  const [entityUnifiedNo, setEntityUnifiedNo] = useState(entity_unified_no || '');
  const [initialVat, setInitialVat] = useState(vat_number || '');
  const [initialCommercial, setInitialCommercial] = useState(commercial_register || '');
  const [initialCompanyName, setInitialCompanyName] = useState(company_name || '');
  const [initialAddress, setInitialAddress] = useState(address || '');
  const [initialEntityUnifiedNo, setInitialEntityUnifiedNo] = useState(entity_unified_no || '');
  const [hasChanges, setHasChanges] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setVatNumber(vat_number || '');
    setCommercialRegister(commercial_register || '');
    setCompanyName(company_name || '');
    setCompanyAddress(address || '');
    setEntityUnifiedNo(entity_unified_no || '');
    setInitialVat(vat_number || '');
    setInitialCommercial(commercial_register || '');
    setInitialCompanyName(company_name || '');
    setInitialAddress(address || '');
    setInitialEntityUnifiedNo(entity_unified_no || '');
  }, [vat_number, commercial_register, company_name, address, entity_unified_no]);

  useEffect(() => {
    setHasChanges(
      vatNumber !== initialVat ||
      commercialRegister !== initialCommercial ||
      companyName !== initialCompanyName ||
      companyAddress !== initialAddress ||
      entityUnifiedNo !== initialEntityUnifiedNo
    );
  }, [vatNumber, commercialRegister, companyName, companyAddress, entityUnifiedNo, initialVat, initialCommercial, initialCompanyName, initialAddress, initialEntityUnifiedNo]);

  const handleSubmit = (e) => {
    e.preventDefault();

    // Convert all values to strings and trim
    const trimmedVat = String(vatNumber || '').trim();
    const trimmedCommercial = String(commercialRegister || '').trim();
    const trimmedCompanyName = String(companyName || '').trim();
    const trimmedAddress = String(companyAddress || '').trim();
    const trimmedUnifiedNo = String(entityUnifiedNo || '').trim();

    // Basic validation (optional)
    if (!trimmedVat && !trimmedCommercial && !trimmedCompanyName && !trimmedAddress && !trimmedUnifiedNo) {
      toast.error('Please provide at least one field');
      return;
    }

    // Show confirmation modal instead of directly submitting
    setShowConfirmation(true);
    setPassword('');
  };

  const handleConfirmSubmit = () => {
    if (!password.trim()) {
      toast.error('Please enter your password');
      return;
    }

    setIsSubmitting(true);

    // Convert all values to strings and trim
    const trimmedVat = String(vatNumber || '').trim();
    const trimmedCommercial = String(commercialRegister || '').trim();
    const trimmedCompanyName = String(companyName || '').trim();
    const trimmedAddress = String(companyAddress || '').trim();
    const trimmedUnifiedNo = String(entityUnifiedNo || '').trim();

    const payload = {
      id,
      vat_number: trimmedVat,
      commercial_register: trimmedCommercial,
      company_name: trimmedCompanyName,
      address: trimmedAddress,
      entity_unified_no: trimmedUnifiedNo,
      password: password,
    };

    dispatch(actions.updateSettingsRequest(payload));

    setInitialVat(vatNumber);
    setInitialCommercial(commercialRegister);
    setInitialCompanyName(companyName);
    setInitialAddress(companyAddress);
    setInitialEntityUnifiedNo(entityUnifiedNo);
    setHasChanges(false);
    setShowConfirmation(false);
    setPassword('');
    setIsSubmitting(false);

    toast.success('تم حفظ إعدادات التسجيل التجاري بنجاح', { position: 'top-center', icon: '✅' });
  };

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
    setPassword('');
  };

  const handleReset = () => {
    setVatNumber(initialVat);
    setCommercialRegister(initialCommercial);
    setCompanyName(initialCompanyName);
    setCompanyAddress(initialAddress);
    setEntityUnifiedNo(initialEntityUnifiedNo);
    setHasChanges(false);
    toast.info('تم التراجع عن التغييرات');
  };

  return (
    <Col xl={12} className="mb-4">
      <div className="d-flex align-items-center mb-3">
        <div className="avatar-sm me-3">
          <span className="avatar-title rounded-circle bg-primary-subtle text-primary font-size-20">
            <i className="bx bx-building"></i>
          </span>
        </div>
        <div>
          <h5 className="mb-1">بيانات التسجيل التجاري / الضريبة</h5>
          <p className="text-muted mb-0">تحرير وعرض رقم السجل التجاري ورقم ضريبة القيمة المضافة</p>
        </div>
      </div>

      <Card className="border shadow-sm" style={{ borderRadius: '12px' }}>
        <CardBody className="p-4">
          <Form onSubmit={handleSubmit}>
            <div className="mb-3">
              <Label for="companyName" className="fw-semibold">
                اسم الشركة (Company Name)
              </Label>
              <Input
                id="companyName"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="أدخل اسم الشركة"
                className="form-control-lg"
                style={{ borderRadius: '8px' }}
              />
            </div>

            <div className="mb-3">
              <Label for="address" className="fw-semibold">
                العنوان (Address)
              </Label>
              <Input
                id="address"
                value={companyAddress}
                onChange={(e) => setCompanyAddress(e.target.value)}
                placeholder="أدخل عنوان الشركة"
                className="form-control-lg"
                style={{ borderRadius: '8px' }}
              />
            </div>

            <div className="mb-3">
              <Label for="entityUnifiedNo" className="fw-semibold">
                رقم الهوية الموحد (Entity Unified No)
              </Label>
              <Input
                id="entityUnifiedNo"
                value={entityUnifiedNo}
                onChange={(e) => setEntityUnifiedNo(e.target.value)}
                placeholder="أدخل رقم الهوية الموحد"
                className="form-control-lg"
                style={{ borderRadius: '8px' }}
              />
            </div>

            <div className="mb-3">
              <Label for="vatNumber" className="fw-semibold">
                رقم ضريبة القيمة المضافة (VAT Number)
              </Label>
              <Input
                id="vatNumber"
                value={vatNumber}
                onChange={(e) => setVatNumber(e.target.value)}
                placeholder="أدخل رقم ضريبة القيمة المضافة"
                className="form-control-lg"
                style={{ borderRadius: '8px' }}
              />
            </div>

            <div className="mb-3">
              <Label for="commercialRegister" className="fw-semibold">
                السجل التجاري (Commercial Register)
              </Label>
              <Input
                id="commercialRegister"
                value={commercialRegister}
                onChange={(e) => setCommercialRegister(e.target.value)}
                placeholder="أدخل رقم السجل التجاري"
                className="form-control-lg"
                style={{ borderRadius: '8px' }}
              />
            </div>

            {hasChanges && (
              <Alert color="warning" className="border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                لديك تغييرات غير محفوظة
              </Alert>
            )}

            <div className="d-flex gap-2 mt-3">
              <Button type="submit" color="success" size="lg" disabled={!hasChanges} style={{ borderRadius: '8px', minWidth: '140px' }}>
                حفظ
              </Button>

              {hasChanges && (
                <Button type="button" color="secondary" size="lg" onClick={handleReset} style={{ borderRadius: '8px' }}>
                  تراجع
                </Button>
              )}

              <Badge color="info" className="ms-auto align-self-center">
                حالياً: {vatNumber || '---'} | {commercialRegister || '---'}
              </Badge>
            </div>
          </Form>
        </CardBody>
      </Card>

      {/* Confirmation Modal */}
      <Modal isOpen={showConfirmation} toggle={handleCloseConfirmation} centered size="lg">
        <ModalHeader toggle={handleCloseConfirmation} className="bg-danger text-white">
          <i className="bx bx-exclamation-circle me-2"></i>
          تحذير هام - تأكيد التغيير الحساس
        </ModalHeader>
        <ModalBody>
          <Alert color="danger" className="mb-3">
            <h5 className="mb-2">
              <i className="bx bx-alert-triangle me-2"></i>
              عملية خطرة - يرجى القراءة بعناية
            </h5>
            <p className="mb-0">
              أنت على وشك تعديل بيانات حساسة جداً تؤثر مباشرة على النظام والفواتير:
            </p>
          </Alert>

          <div className="bg-light p-3 rounded mb-3" style={{ borderLeft: '4px solid #dc3545' }}>
            <h6 className="mb-2 fw-bold">عواقب التغيير:</h6>
            <ul className="mb-0">
              <li>تعديل اسم الشركة قد يؤثر على جميع المستندات والفواتير المستقبلية</li>
              <li>تغيير رقم الضريبة قد يؤدي إلى مشاكل في المحاسبة والتقارير الضريبية</li>
              <li>تعديل رقم السجل التجاري يؤثر على سجلات الشركة</li>
              <li>تغيير البيانات قد يتطلب تعديلات إضافية في الفواتير القديمة</li>
              <li>هذا التغيير لا يمكن التراجع عنه بسهولة</li>
            </ul>
          </div>

          <div className="mb-3">
            <Label for="confirmPassword" className="fw-semibold text-danger">
              ✓ أدخل كلمة المرور لتأكيد التغيير
            </Label>
            <Input
              id="confirmPassword"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="أدخل كلمة المرور الخاصة بك"
              className="form-control-lg"
              style={{ borderRadius: '8px', borderColor: '#dc3545' }}
              onKeyPress={(e) => e.key === 'Enter' && handleConfirmSubmit()}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleCloseConfirmation} style={{ borderRadius: '8px' }}>
            إلغاء
          </Button>
          <Button
            color="danger"
            onClick={handleConfirmSubmit}
            disabled={!password.trim() || isSubmitting}
            style={{ borderRadius: '8px', minWidth: '140px' }}
          >
            {isSubmitting ? 'جاري الحفظ...' : 'تأكيد التغيير'}
          </Button>
        </ModalFooter>
      </Modal>
    </Col>
  );
}
